import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Staff } from '../types';
import { adminLogin } from '../api/admin';

interface AdminContextType {
  staff: Staff | null;
  loading: boolean;
  login: (email: string) => Promise<boolean>;
  logout: () => void;
  isAuthenticated: boolean;
}

const AdminContext = createContext<AdminContextType | undefined>(undefined);

export const AdminProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [staff, setStaff] = useState<Staff | null>(() => {
    const saved = localStorage.getItem('ff_admin_staff');
    return saved ? JSON.parse(saved) : null;
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (staff) {
      localStorage.setItem('ff_admin_staff', JSON.stringify(staff));
    } else {
      localStorage.removeItem('ff_admin_staff');
    }
  }, [staff]);

  const login = async (email: string) => {
    setLoading(true);
    try {
      const user = await adminLogin(email);
      if (user) {
        setStaff(user);
        return true;
      }
      return false;
    } catch (e) {
      console.error(e);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    setStaff(null);
  };

  return (
    <AdminContext.Provider value={{ staff, loading, login, logout, isAuthenticated: !!staff }}>
      {children}
    </AdminContext.Provider>
  );
};

export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (!context) throw new Error('useAdmin must be used within an AdminProvider');
  return context;
};
